/* eslint-disable formatjs/no-literal-string-in-jsx */

import React from 'react'
import classNames from 'classnames'
import { motion } from 'motion/react'
import { FormattedMessage } from 'react-intl'

import {
  BlockCodeIcon,
  BulbIcon,
  type Icon,
  LockIcon,
  PeopleIcon,
  QuestionRoundIcon,
} from '@slite/icons'

type CompanyFolder = {
  id: string
  title: string
  description: string
  Icon: Icon
}

const COMPANY_FOLDERS: CompanyFolder[] = [
  {
    id: 'help-center',
    title: 'Help Center',
    description:
      'User guides for getting started, questions and troubleshooting.',
    Icon: QuestionRoundIcon,
  },
  {
    id: 'compliance',
    title: 'Compliance',
    description:
      'Policies, procedures, and frameworks that keep us audit-ready.',
    Icon: LockIcon,
  },
  {
    id: 'engineering',
    title: 'Engineering',
    description:
      'How we build, deploy, and operate everything that runs The Nice Co..',
    Icon: BlockCodeIcon,
  },
  {
    id: 'product',
    title: 'Product',
    description: 'Roadmap, changelog, and the decisions behind what we ship.',
    Icon: BulbIcon,
  },
  {
    id: 'people-ops',
    title: 'People & Ops',
    description:
      'Hiring, onboarding, and the handbook for working at The Nice Co..',
    Icon: PeopleIcon,
  },
]

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
}

const blockVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.4, 0, 0.2, 1] as const },
  },
}

export const CompanyOverview = () => (
  <motion.div
    key="company-overview"
    className="simple-rich-text"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    <motion.h1
      className="shade-05"
      variants={blockVariants}
      style={{ fontSize: 32 }}
    >
      <FormattedMessage
        id="new.hero.demo.company.title"
        defaultMessage="Our Knowledge Base"
      />
    </motion.h1>
    <motion.div
      variants={blockVariants}
      className={classNames(
        'flexbox flexbox-column padding-vertical-16 padding-left-20 padding-right-8 margin-vertical-16',
        'background-shade-60 border-small border-shade-45'
      )}
      style={{ borderRadius: 16 }}
    >
      <div className="font-size-6 shade-20">
        <span
          className="margin-right-8"
          style={{ color: 'black', fontSize: 18 }}
          aria-hidden
        >
          👋
        </span>
        <FormattedMessage
          id="new.hero.demo.company.welcome"
          defaultMessage="Welcome to our Handbook, the go-to place for anything related to you and the way we work!"
        />
      </div>
    </motion.div>
    <motion.h2 className="shade-05" variants={blockVariants}>
      <FormattedMessage
        id="new.hero.demo.company.overview"
        defaultMessage="Overview"
      />
    </motion.h2>
    <motion.p className="shade-10" variants={blockVariants}>
      <FormattedMessage
        id="new.hero.demo.company.intro"
        defaultMessage="Everything we know, organized by team. Browse the sections below to find what you need."
      />
    </motion.p>
    <div className="flexbox flexbox-column margin-top-16" style={{ gap: 12 }}>
      {COMPANY_FOLDERS.map(({ id, title, description, Icon }) => (
        <motion.div
          key={id}
          variants={blockVariants}
          className="flexbox flexbox-align-center"
          style={{
            background: 'white',
            borderRadius: 12,
            padding: '10px 12px',
            border: '1px solid var(--shade-45)',
            gap: 16,
          }}
        >
          <div
            className="flexbox flexbox-centered flexbox-no-shrink"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--shade-50)',
              color: 'var(--shade-20)',
            }}
          >
            <Icon size={16} />
          </div>
          <div className="flexbox-grow" style={{ minWidth: 0 }}>
            <div className="font-size-6 font-weight-semibold shade-05">
              {title}
            </div>
            <div className="font-size-7 shade-20 margin-top-2">
              {description}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </motion.div>
)
